import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { analytics } from '../utils/analytics';

const AuthContext = createContext(null);

const API = '/api';

// Send cookies with every request (httpOnly auth cookie)
axios.defaults.withCredentials = true;

// Token-based auth fallback: for environments where cookies are blocked (e.g. webviews/iframes)
// Only used when httpOnly cookies fail. Stored with timestamp for staleness check.
const TOKEN_KEY = 'auth_token_fallback';
const TOKEN_TS_KEY = 'auth_token_ts';

function getStoredToken() {
  const ts = sessionStorage.getItem(TOKEN_TS_KEY);
  // Expire stored token after 1 hour (reduced surface for XSS)
  if (ts && Date.now() - parseInt(ts) > 60 * 60 * 1000) {
    clearStoredToken();
    return null;
  }
  return sessionStorage.getItem(TOKEN_KEY);
}
function storeToken(token) {
  // Only store if httpOnly cookies aren't available (cookie-blocked environments)
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem(TOKEN_TS_KEY, Date.now().toString());
  }
}
function clearStoredToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_TS_KEY);
}

// Attach Authorization header from stored token on every request
axios.interceptors.request.use((config) => {
  if (!config.headers['Authorization']) {
    const token = getStoredToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

// CSRF double-submit: read csrf_token cookie and attach as X-CSRF-Token header
// on all state-changing requests (POST, PUT, DELETE, PATCH)
function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

axios.interceptors.request.use((config) => {
  const method = (config.method || '').toUpperCase();
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const csrfToken = getCookie('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

// CSRF auto-retry: if a request fails with 403 CSRF, the backend sets a fresh
// csrf_token cookie on the error response. Read it and retry once.
// Also: 401 auto-refresh — if access token expired, try refresh before logout.
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // CSRF retry
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail === 'CSRF validation failed' &&
      !original._csrfRetried
    ) {
      original._csrfRetried = true;
      const freshToken = getCookie('csrf_token');
      if (freshToken) {
        original.headers['X-CSRF-Token'] = freshToken;
        return axios(original);
      }
    }

    // Token refresh on 401 (skip for auth endpoints themselves)
    if (
      error.response?.status === 401 &&
      !original._refreshRetried &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/signup')
    ) {
      original._refreshRetried = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const res = await axios.post(`${API}/auth/refresh`);
          if (res.data.token) {
            storeToken(res.data.token);
            onRefreshed(res.data.token);
          }
          isRefreshing = false;
          original.headers['Authorization'] = `Bearer ${res.data.token}`;
          return axios(original);
        } catch {
          isRefreshing = false;
          refreshSubscribers = [];
          // Refresh failed — token is truly expired, let it propagate
          return Promise.reject(error);
        }
      } else {
        // Another refresh is in progress — queue this request
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            original.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(axios(original));
          });
        });
      }
    }

    return Promise.reject(error);
  }
);

// Session timeout: 30 minutes of inactivity
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Users excluded from all engagement tracking (views, clicks, etc.)
const TRACKING_EXCLUDED_EMAILS = new Set([
  'charlie.wood@discoveressex.co.uk',
]);

const TRACKING_URL_PATTERNS = ['/view', '/click', '/track-click', '/banner-click'];

let trackingInterceptorId = null;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    localStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  // Check for session timeout
  useEffect(() => {
    if (!user) return;

    const checkTimeout = () => {
      const storedLastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
      const lastActivityTime = Math.max(lastActivity, storedLastActivity);
      const timeSinceActivity = Date.now() - lastActivityTime;
      
      if (timeSinceActivity > SESSION_TIMEOUT) {
        toast.warning('Session expired due to inactivity. Please login again.');
        logout();
      }
    };

    const intervalId = setInterval(checkTimeout, 60 * 1000);

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      clearInterval(intervalId);
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [user, lastActivity, updateActivity]);

  // On mount, check auth status via cookie
  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setupTrackingExclusion(response.data);
      updateActivity();
    } catch (error) {
      // Not authenticated — cookie missing or expired
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const setupTrackingExclusion = (userData) => {
    if (trackingInterceptorId !== null) {
      axios.interceptors.request.eject(trackingInterceptorId);
      trackingInterceptorId = null;
    }
    if (userData && TRACKING_EXCLUDED_EMAILS.has(userData.email?.toLowerCase())) {
      trackingInterceptorId = axios.interceptors.request.use((config) => {
        const url = config.url || '';
        if (TRACKING_URL_PATTERNS.some(p => url.includes(p))) {
          const controller = new AbortController();
          controller.abort();
          config.signal = controller.signal;
        }
        return config;
      });
    }
  };

  const login = async (email, password, captcha_id = null, captcha_answer = null) => {
    const loginData = { email, password };
    if (captcha_id && captcha_answer !== null) {
      loginData.captcha_id = captcha_id;
      loginData.captcha_answer = captcha_answer;
    }
    // Include trusted device token if available (for admin MFA bypass)
    const savedDeviceToken = localStorage.getItem('admin_device_token');
    if (savedDeviceToken) {
      loginData.device_token = savedDeviceToken;
    }
    const response = await axios.post(`${API}/auth/login`, loginData);
    // Store token for header-based auth fallback
    if (response.data.token) {
      storeToken(response.data.token);
    }
    // Cookie is set by the backend via Set-Cookie header
    if (response.data.user) {
      setUser(response.data.user);
      setupTrackingExclusion(response.data.user);
      localStorage.setItem('lastActivity', Date.now().toString());
      analytics.login();
    }
    return response.data;
  };

  // Set auth state after MFA completion
  const setAuthState = (userData, token) => {
    setUser(userData);
    setupTrackingExclusion(userData);
    if (token) storeToken(token);
    localStorage.setItem('lastActivity', Date.now().toString());
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore errors — clear local state regardless
    }
    setUser(null);
    clearStoredToken();
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('admin_device_token');
    if (trackingInterceptorId !== null) {
      axios.interceptors.request.eject(trackingInterceptorId);
      trackingInterceptorId = null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, setAuthState, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
